import { Link } from "react-router-dom";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-paper dark:bg-dark-bg transition-colors px-6 py-16">
      <div className="w-full max-w-3xl mx-auto bg-white dark:bg-dark-surface border border-hairline dark:border-dark-border rounded-lg p-8 md:p-12">

        {/* Back nav */}
        <div className="mb-8">
          <Link to="/" className="text-ink hover:underline text-sm">
            ← Back to ZUPA
          </Link>
        </div>

        {/* Title */}
        <h1 className="font-display text-2xl sm:text-3xl text-ink dark:text-dark-text mb-2 leading-snug">
          Privacy Policy
        </h1>
        <p className="text-steel-dark dark:text-dark-muted mb-1">
          <strong>Last Updated:</strong> April 12, 2026
        </p>
        <p className="text-steel-dark dark:text-dark-muted mb-10">
          <strong>Effective Date:</strong> April 12, 2026
        </p>

        <hr className="border-hairline dark:border-dark-border mb-10" />

        {/* Who We Are */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mb-4">
            Who We Are
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            ZUPA ("we," "us," "our") is a product of <strong>Summerhill Technologies</strong>, a company incorporated in India. We operate the website{" "}
            <a href="https://zupapost.ai" target="_blank" rel="noopener noreferrer" className="text-ink hover:underline">zupapost.ai</a>{" "}
            and the ZUPA platform — an AI-powered social media content generation service.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>Registered Address:</strong> Summerhill Technologies Pvt. Ltd., Top Floor, P K Apartments, Dyerton Estate, Khalini, Shimla, Himachal Pradesh 171002, India
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>Data Protection Officer:</strong> Manish Sharma —{" "}
            <a href="mailto:info@summerhilltechnologies.com" className="text-ink hover:underline">info@summerhilltechnologies.com</a>
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>Grievance Officer (India):</strong> Manish Sharma, Privacy Officer —{" "}
            <a href="mailto:info@summerhilltechnologies.com" className="text-ink hover:underline">info@summerhilltechnologies.com</a>
          </p>
          <p className="text-steel-dark dark:text-dark-muted">
            This Privacy Policy explains how we collect, use, store, share, and protect your personal data when you use ZUPA. It applies to all users regardless of location, including users in the European Union/European Economic Area, India, the United Arab Emirates, Saudi Arabia, and all other jurisdictions.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border mb-10" />

        {/* Section 1 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mb-4">
            1. Data We Collect
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-6">
            We collect the following categories of personal data:
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mb-2">
            1.1 Account Information
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-6">
            When you create an account, we collect your full name, email address, and password (stored as a bcrypt hash — we never store your actual password). If you sign up using Google, we receive your Google ID, name, email, and profile picture from Google's OAuth service.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mb-2">
            1.2 Workspace and Business Data
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-6">
            When you set up a workspace, you may provide your company name, website URL, industry, company description, target audience, brand voice guidelines, writing samples, founder story, key phrases, and service descriptions. You may also provide brand visual data including logo, brand colors, visual style preferences, photography style, and visual mood.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mb-2">
            1.3 Audience Persona Data
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-6">
            You may create audience personas within your workspace, which can include persona names, descriptions, age ranges, job titles, seniority levels, industries, location tendencies, motivations, frustrations, values, aspirations, engagement triggers, content preferences, and platform behavior profiles.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mb-2">
            1.4 Social Media Account Data
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-6">
            When you connect social media accounts (LinkedIn, Instagram, Twitter/X, Facebook), we store OAuth authentication tokens that allow us to post content on your behalf. These tokens are encrypted at rest using AES-256 encryption. We also store your platform username, display name, and profile picture for display within the app.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mb-2">
            1.5 Content and Usage Data
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-6">
            We store the prompts you submit for content generation, the AI-generated posts and images created for you, saved prompts, calendar notes, post examples you provide for learning, and your platform preferences and tone selections. We also record usage metrics such as generation counts, posting history, and feature usage.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mb-2">
            1.6 Payment Data
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-6">
            We use Razorpay as our payment processor. We store your Razorpay customer ID, subscription ID, and order references. <strong>We do not store your credit card number, bank account details, or any payment card information.</strong> All payment data is processed and stored by Razorpay under their own security and privacy standards.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mb-2">
            1.7 Technical and Security Data
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-6">
            We collect your IP address, browser type, and device information for security and fraud prevention purposes. We generate a device fingerprint (stored as a one-way SHA-256 hash) to prevent trial abuse. We store session data and authentication tokens to keep you logged in.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mb-2">
            1.8 Client-Side Storage
          </h3>
          <p className="text-steel-dark dark:text-dark-muted">
            We store the following data in your browser's local storage: your authentication token (for keeping you logged in), active workspace ID, basic profile information (name, email, subscription status), and cached post data. This data remains on your device until you log out.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border mb-10" />

        {/* Section 2 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mb-4">
            2. How We Use Your Data
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            We use your personal data for the following purposes:
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>To provide the ZUPA service:</strong> Your prompts, workspace data, brand voice, writing samples, audience personas, and service descriptions are used to generate personalized social media content. This involves sending your data to our AI provider (see Section 3).
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>To manage your account:</strong> Your email and name are used for account creation, authentication, password resets, and account communications.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>To process payments:</strong> Your email and name are shared with Razorpay to manage subscriptions and billing.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>To post content on your behalf:</strong> When you choose to publish content, your connected social media account tokens are used to post to the platforms you select.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>To improve the service:</strong> Usage metrics and anonymized content performance data help us improve content quality. We maintain a bank of high-performing post examples with all identifying information removed.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>To ensure security:</strong> Device fingerprints, IP addresses, and session data are used to prevent fraud, detect abuse, and protect your account.
          </p>
          <p className="text-steel-dark dark:text-dark-muted">
            <strong>To communicate with you:</strong> We send transactional emails (verification, password reset, trial reminders) via our email provider Resend. Marketing emails are opt-in only.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border mb-10" />

        {/* Section 3 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mb-4">
            3. AI Processing and Third-Party Data Sharing
          </h2>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mb-2">
            3.1 Google Gemini (AI Content Generation)
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-3">
            <strong>This is important — please read carefully.</strong>
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-3">
            When you generate content using ZUPA, the following data is sent to Google's Gemini AI API for processing:
          </p>
          <ul className="list-disc list-inside text-steel-dark dark:text-dark-muted mb-3 space-y-1 pl-2">
            <li>Your prompt text</li>
            <li>Your company name, description, industry, and target audience</li>
            <li>Your brand voice guidelines and writing samples (up to 2,000 characters each)</li>
            <li>Your founder story and key phrases (if provided)</li>
            <li>Your service names and descriptions</li>
            <li>Your audience persona profiles (demographics, psychographics, behavioral data)</li>
            <li>Post examples you have provided for learning</li>
          </ul>
          <p className="text-steel-dark dark:text-dark-muted mb-3">
            This data is transmitted to Google's servers to generate your social media content. Google processes this data under their{" "}
            <a href="https://ai.google.dev/gemini-api/terms" target="_blank" rel="noopener noreferrer" className="text-ink hover:underline">Gemini API Terms of Service</a>.
            We use the Gemini API's paid tier, under which Google states they do not use your prompts or responses to train their models.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-6">
            We configure safety filters on all AI generation requests to block harmful content including hate speech and explicit material.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mb-2">
            3.2 Social Media Platforms
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-2">
            When you connect accounts and publish content:
          </p>
          <ul className="list-disc list-inside text-steel-dark dark:text-dark-muted mb-6 space-y-1 pl-2">
            <li><strong>LinkedIn:</strong> Post content, images, and your access token. Scopes: profile access, social posting.</li>
            <li><strong>Twitter/X:</strong> Tweet content and your access token.</li>
            <li><strong>Facebook:</strong> Post content, images, and your page token. Scopes: page reading, post management.</li>
            <li><strong>Instagram:</strong> Captions, images, and your access token. Scopes: business content publishing.</li>
          </ul>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mb-2">
            3.3 Razorpay (Payments)
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-6">
            We share your name and email with Razorpay for payment processing. Razorpay processes payments under their own{" "}
            <a href="https://razorpay.com/privacy/" target="_blank" rel="noopener noreferrer" className="text-ink hover:underline">Privacy Policy</a>.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mb-2">
            3.4 Pexels and Unsplash (Image Sourcing)
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-6">
            When image suggestions are generated, AI-derived search keywords (not your raw prompt text) are sent to Pexels and Unsplash APIs to source relevant stock images.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mb-2">
            3.5 Resend (Email Communications)
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-6">
            Your email address and name are shared with Resend for sending transactional and, if opted in, marketing emails.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mb-2">
            3.6 Analytics and Tracking
          </h3>
          <p className="text-steel-dark dark:text-dark-muted">
            We do not use any third-party analytics, tracking scripts, advertising pixels, or similar tracking technologies.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border mb-10" />

        {/* Section 4 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mb-4">
            4. Legal Basis for Processing
          </h2>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mb-2">
            For users in the European Union / European Economic Area (GDPR)
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-3">
            We process your personal data on the following legal bases:
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-3">
            <strong>Contractual necessity (Article 6(1)(b)):</strong> Processing your account data, workspace data, prompts, and generating content is necessary to provide the service you subscribed to. Processing payment data is necessary to manage your subscription.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-3">
            <strong>Consent (Article 6(1)(a)):</strong> Connecting social media accounts and posting on your behalf requires your explicit consent, which you provide when connecting each account. Marketing emails require your opt-in consent.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-6">
            <strong>Legitimate interest (Article 6(1)(f)):</strong> Processing IP addresses and device fingerprints for security, fraud prevention, and trial abuse detection is based on our legitimate interest in protecting the service and our users. You may object to this processing (see Section 6).
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mb-2">
            For users in India (DPDP Act 2023)
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-6">
            We process your digital personal data based on your consent, which you provide at registration and when using specific features. You may withdraw your consent at any time (see Section 6). Certain processing related to security and fraud prevention is based on deemed consent for legitimate uses under the Act.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mb-2">
            For users in the UAE and Saudi Arabia
          </h3>
          <p className="text-steel-dark dark:text-dark-muted">
            We process your personal data based on your consent and as necessary for the performance of our contract with you. Cross-border data transfers are conducted with appropriate safeguards as described in Section 5.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border mb-10" />

        {/* Section 5 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mb-4">
            5. Cross-Border Data Transfers
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            Summerhill Technologies is based in India. Your data may be transferred to and processed in countries outside your country of residence, including:
          </p>
          <ul className="list-disc list-inside text-steel-dark dark:text-dark-muted mb-4 space-y-1 pl-2">
            <li><strong>United States:</strong> Data sent to Google Gemini API is processed on Google's infrastructure, which may include servers in the United States and other countries.</li>
            <li><strong>India:</strong> Our primary servers and team are based in India.</li>
          </ul>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            For transfers from the EU/EEA, we rely on Standard Contractual Clauses (SCCs) approved by the European Commission or adequacy decisions where applicable.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            For transfers from Saudi Arabia, we comply with the requirements of the PDPL and its Transfer Regulations. We ensure appropriate safeguards are in place for any data transferred outside the Kingdom.
          </p>
          <p className="text-steel-dark dark:text-dark-muted">
            For transfers from the UAE, we rely on adequacy determinations, standard contractual clauses, or your explicit consent as appropriate.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border mb-10" />

        {/* Section 6 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mb-4">
            6. Your Rights
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            Regardless of where you are located, you have the following rights regarding your personal data:
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>Right to Access:</strong> You can request a copy of all personal data we hold about you. We provide a data export feature accessible at Settings &gt; Export Data, which delivers your data in a machine-readable JSON format.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>Right to Rectification:</strong> You can correct your personal data at any time through your account settings, workspace settings, and persona management.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>Right to Erasure:</strong> You can delete your account at any time from Settings. When you delete your account, we permanently remove all your personal data including your profile, workspaces, generated content, social connections (tokens are revoked on the platform side), saved prompts, usage data, device fingerprints associated with your account, cached data, and scoring logs. This deletion is permanent and cannot be reversed. Workspaces can be individually deleted with a 20-day recovery period before permanent deletion.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>Right to Data Portability:</strong> You can export your data in a structured, machine-readable format using the data export feature.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>Right to Restrict Processing:</strong> You may request that we limit how we process your data in certain circumstances.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>Right to Object:</strong> You may object to processing based on legitimate interests, including device fingerprinting for fraud prevention.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>Right to Withdraw Consent:</strong> You may withdraw your consent for any processing at any time. This does not affect the lawfulness of processing conducted before withdrawal. You can disconnect social media accounts at any time; we will revoke the tokens on the platform side and remove them from our systems.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>Right to Not Be Subject to Solely Automated Decisions:</strong> ZUPA uses AI to generate content suggestions, but you always review and decide whether to use, edit, or publish any generated content. No automated decisions with legal or similarly significant effects are made about you.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>Right to Nominate (India DPDP Act):</strong> Indian users may nominate another individual to exercise their data rights on their behalf in the event of death or incapacity, as provided under the DPDP Act.
          </p>
          <p className="text-steel-dark dark:text-dark-muted">
            <strong>How to exercise your rights:</strong> Contact us at{" "}
            <a href="mailto:info@summerhilltechnologies.com" className="text-ink hover:underline">info@summerhilltechnologies.com</a>.
            We will respond within 30 days (EU) or within the timeframe mandated by your local law. We may need to verify your identity before processing your request.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border mb-10" />

        {/* Section 7 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mb-4">
            7. Data Retention
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            We retain your data for the following periods:
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>Account data:</strong> For the duration of your account plus 30 days after account deletion to complete any pending processes.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>Generated posts:</strong> Posts are automatically deleted 60 days after creation unless saved to a collection. Favorited or saved posts are retained until you delete them or your account is deleted.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>Workspace data:</strong> Retained for the duration of your account. Deleted workspaces enter a 20-day recovery period before permanent deletion.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>Payment records:</strong> Razorpay transaction references are retained for 7 years to comply with tax and legal requirements.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>Authentication tokens:</strong> JWT tokens expire after 7 days (or 30 days with "remember me" enabled). Email verification tokens expire after 24 hours. Password reset tokens expire after 1 hour.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>Session and cache data:</strong> Redis session data expires after 24 hours. Cached generation data expires after 24 hours.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>Security data:</strong> Device fingerprint hashes are retained for the duration of your account and deleted upon account deletion.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>Post scoring and feedback data:</strong> Retained for the duration of your account and deleted upon account deletion.
          </p>
          <p className="text-steel-dark dark:text-dark-muted">
            <strong>Anonymized data:</strong> High-performing post content that has been fully anonymized (with no link to any user account) may be retained indefinitely to improve service quality.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border mb-10" />

        {/* Section 8 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mb-4">
            8. Data Security
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            We implement the following measures to protect your personal data:
          </p>
          <ul className="list-disc list-inside text-steel-dark dark:text-dark-muted mb-4 space-y-2 pl-2">
            <li>All data in transit is encrypted using TLS/HTTPS with HSTS enforcement.</li>
            <li>Passwords are hashed using bcrypt with 12 rounds.</li>
            <li>Social media OAuth tokens are encrypted at rest using Fernet (AES-128-CBC) symmetric encryption.</li>
            <li>API keys and secrets are stored in environment variables, never in source code.</li>
            <li>Authentication endpoints are rate-limited to prevent brute-force attacks.</li>
            <li>We enforce role-based access controls for administrative functions.</li>
            <li>We conduct regular security reviews of our codebase and infrastructure.</li>
          </ul>
          <p className="text-steel-dark dark:text-dark-muted">
            Despite these measures, no method of electronic transmission or storage is 100% secure. We cannot guarantee absolute security, but we are committed to protecting your data using industry-standard practices.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border mb-10" />

        {/* Section 9 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mb-4">
            9. AI-Generated Content Disclosure
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            ZUPA uses artificial intelligence (Google's Gemini AI) to generate social media content. All content produced by ZUPA is AI-generated.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            We apply behavioral psychology principles (based on research by scholars such as Robert Cialdini and Daniel Kahneman) to optimize content for engagement. The selection and application of these principles is performed by AI. <strong>This does not constitute professional psychological, marketing, or business advice.</strong>
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-3">
            Under the EU AI Act, we disclose the following:
          </p>
          <ul className="list-disc list-inside text-steel-dark dark:text-dark-muted space-y-1 pl-2">
            <li>Content generated by ZUPA is produced by an AI system.</li>
            <li>When you interact with ZUPA's content generation features, you are interacting with AI.</li>
            <li>AI-generated images are produced using Google's image generation capabilities and may not be unique.</li>
            <li>You are responsible for reviewing all AI-generated content before publishing.</li>
          </ul>
        </section>

        <hr className="border-hairline dark:border-dark-border mb-10" />

        {/* Section 10 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mb-4">
            10. Children's Data
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            ZUPA is not intended for use by individuals under the age of 18. We do not knowingly collect personal data from children. If you are under 18, please do not register for an account or use our service.
          </p>
          <p className="text-steel-dark dark:text-dark-muted">
            If we discover that we have collected personal data from a child under 18, we will delete that data promptly. If you believe a child has provided us with personal data, please contact us at{" "}
            <a href="mailto:info@summerhilltechnologies.com" className="text-ink hover:underline">info@summerhilltechnologies.com</a>.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border mb-10" />

        {/* Section 11 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mb-4">
            11. Data Breach Notification
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            In the event of a personal data breach that poses a risk to your rights and freedoms, we will:
          </p>
          <ul className="list-disc list-inside text-steel-dark dark:text-dark-muted space-y-2 pl-2">
            <li>Notify the relevant supervisory authority within 72 hours of becoming aware of the breach (as required by GDPR, India DPDP Act, and UAE/Saudi regulations).</li>
            <li>Notify affected users without undue delay if the breach is likely to result in a high risk to their rights and freedoms.</li>
            <li>Document all breaches and the remedial actions taken.</li>
          </ul>
        </section>

        <hr className="border-hairline dark:border-dark-border mb-10" />

        {/* Section 12 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mb-4">
            12. Cookies and Local Storage
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            ZUPA does not use traditional browser cookies for tracking or analytics. We do not use any third-party tracking technologies.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-3">
            We use your browser's local storage to store:
          </p>
          <ul className="list-disc list-inside text-steel-dark dark:text-dark-muted mb-4 space-y-1 pl-2">
            <li>Your authentication token (strictly necessary for the service to function)</li>
            <li>Your active workspace ID (strictly necessary)</li>
            <li>Cached profile and post data (functional — improves performance)</li>
          </ul>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>Strictly necessary storage</strong> is required for the service to operate and does not require consent. <strong>Functional storage</strong> is used to improve your experience. You can clear your browser's local storage at any time through your browser settings, though this will log you out.
          </p>
          <p className="text-steel-dark dark:text-dark-muted">
            We do not store any data in your browser for advertising, analytics, or tracking purposes.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border mb-10" />

        {/* Section 13 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mb-4">
            13. Third-Party Links and Services
          </h2>
          <p className="text-steel-dark dark:text-dark-muted">
            ZUPA may contain links to third-party websites or integrate with third-party services (social media platforms, payment processors). We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies before providing them with any personal data.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border mb-10" />

        {/* Section 14 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mb-4">
            14. Changes to This Policy
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of material changes by:
          </p>
          <ul className="list-disc list-inside text-steel-dark dark:text-dark-muted mb-4 space-y-1 pl-2">
            <li>Displaying a notice within the ZUPA application</li>
            <li>Sending an email to your registered email address (for significant changes)</li>
            <li>Updating the "Last Updated" date at the top of this policy</li>
          </ul>
          <p className="text-steel-dark dark:text-dark-muted">
            Your continued use of ZUPA after changes are posted constitutes acceptance of the updated policy. If you disagree with any changes, you may delete your account at any time.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border mb-10" />

        {/* Section 15 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mb-4">
            15. Region-Specific Information
          </h2>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mb-2">
            European Union / European Economic Area
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-3">
            <strong>Data Controller:</strong> Summerhill Technologies Pvt. Ltd., Top Floor, P K Apartments, Dyerton Estate, Khalini, Shimla, Himachal Pradesh 171002, India.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-3">
            <strong>EU Representative (GDPR Article 27):</strong> Rahul Dogar —{" "}
            <a href="mailto:rahulsdogar@gmail.com" className="text-ink hover:underline">rahulsdogar@gmail.com</a>.
            Our EU representative can be contacted for any matters relating to the processing of personal data of EU/EEA residents.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-3">
            <strong>Supervisory Authority:</strong> You have the right to lodge a complaint with your local data protection authority. A list of EU DPAs is available at{" "}
            <a href="https://edpb.europa.eu/about-edpb/about-edpb/members_en" target="_blank" rel="noopener noreferrer" className="text-ink hover:underline">edpb.europa.eu</a>.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-6">
            <strong>Data Protection Impact Assessment:</strong> We have conducted assessments of our AI processing activities to ensure they comply with GDPR requirements.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mb-2">
            India
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-3">
            <strong>Data Fiduciary:</strong> Summerhill Technologies.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-3">
            <strong>Grievance Officer:</strong> Manish Sharma, Privacy Officer —{" "}
            <a href="mailto:info@summerhilltechnologies.com" className="text-ink hover:underline">info@summerhilltechnologies.com</a>{" "}
            — You may contact our Grievance Officer for any concerns regarding the processing of your personal data. We will acknowledge your grievance within 48 hours and resolve it within the timeframe prescribed by the DPDP Act and Rules.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-3">
            <strong>Address for privacy matters:</strong> Privacy Officer, Summerhill Technologies Pvt. Ltd., Top Floor, P K Apartments, Dyerton Estate, Khalini, Shimla, Himachal Pradesh 171002, India.
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-6">
            <strong>Consent Withdrawal:</strong> You may withdraw your consent at any time by contacting us or using the relevant feature in your account settings. Withdrawal of consent will not affect the lawfulness of processing based on consent before its withdrawal.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mb-2">
            United Arab Emirates
          </h3>
          <p className="text-steel-dark dark:text-dark-muted mb-6">
            We comply with Federal Decree-Law No. 45 of 2021 on the Protection of Personal Data. You may exercise your rights under the UAE PDPL by contacting us at{" "}
            <a href="mailto:info@summerhilltechnologies.com" className="text-ink hover:underline">info@summerhilltechnologies.com</a>.
          </p>

          <h3 className="text-lg font-semibold text-ink dark:text-dark-text mb-2">
            Kingdom of Saudi Arabia
          </h3>
          <p className="text-steel-dark dark:text-dark-muted">
            We comply with the Personal Data Protection Law (PDPL) as enforced by SDAIA. Data transfers outside the Kingdom are conducted with appropriate safeguards. You may exercise your rights under the Saudi PDPL by contacting us at{" "}
            <a href="mailto:info@summerhilltechnologies.com" className="text-ink hover:underline">info@summerhilltechnologies.com</a>.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border mb-10" />

        {/* Section 16 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-ink dark:text-dark-text mb-4">
            16. Contact Us
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            For any questions, concerns, or requests regarding this Privacy Policy or your personal data:
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-2">
            <strong>Data Protection Officer:</strong> Manish Sharma —{" "}
            <a href="mailto:info@summerhilltechnologies.com" className="text-ink hover:underline">info@summerhilltechnologies.com</a>
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-2">
            <strong>General Contact:</strong>{" "}
            <a href="mailto:info@coderootz.com" className="text-ink hover:underline">info@coderootz.com</a>
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-2">
            <strong>Business Contact:</strong>{" "}
            <a href="mailto:bd@summerhilltechnologies.com" className="text-ink hover:underline">bd@summerhilltechnologies.com</a>
          </p>
          <p className="text-steel-dark dark:text-dark-muted mb-4">
            <strong>Website:</strong>{" "}
            <a href="https://zupapost.ai" target="_blank" rel="noopener noreferrer" className="text-ink hover:underline">zupapost.ai</a>
          </p>
          <p className="text-steel-dark dark:text-dark-muted">
            We aim to respond to all privacy-related inquiries within 30 days.
          </p>
        </section>

        <hr className="border-hairline dark:border-dark-border mb-10" />

        <p className="text-sm text-steel-dark dark:text-dark-muted italic">
          This Privacy Policy is provided for informational purposes. We recommend that users with specific legal concerns consult with a qualified legal professional.
        </p>
      </div>
    </div>
  );
}
